import {createSelector, createSlice} from '@reduxjs/toolkit';
const slice = createSlice({
  name: 'auth',
  initialState: {
    isAuthenticated: false,
    userData: {},
    onboardingProgress: 0.0,
    currentNavigationTab: [],
    loginRoute: '',
    pageError: null,
  },
  reducers: {
    loginSuccess: (state, action) => {
      return {
        ...state,
        userData: action.payload.userData,
        isAuthenticated: true,
      };
    },
    logout: state => {
      localStorage.setItem('login', 'loggedOut');
      localStorage.removeItem('token');
      return {
        ...state,
        isAuthenticated: false,
        userData: {},
      };
    },
    setOnboardingProgress: (state, action) => {
      return {
        ...state,
        onboardingProgress: action.payload,
      };
    },
    setTempUserData: (state, action) => {
      state.userData = {...state.userData, ...action.payload.userData};
    },
    handlePageError: (state, action) => {
      return {
        ...state,
        pageError: action.payload.error,
      };
    },
    handleClearPageError: state => {
      return {
        ...state,
        pageError: null,
      };
    },
    handleNavigateToHome: (state, action) => {
      return {
        ...state,
        loginRoute: action.payload.route,
      };
    },
  },
});

const FULL_ACCESS = {
  view: true,
  update: true,
  delete: true,
  export: true,
  create: true,
};

// Map a raw operations array to the boolean shape every call site consumes.
// `ALL` short-circuits to grant everything for that scope.
const operationsToBooleans = (operations = []) => {
  const all = operations.includes('ALL') ? true : null;
  return {
    view: all ?? operations.includes('VIEW'),
    update: all ?? operations.includes('UPDATE'),
    delete: all ?? operations.includes('DELETE'),
    export: all ?? operations.includes('EXPORT'),
    create: all ?? operations.includes('CREATE'),
  };
};

const isSuperRole = activeAdminTab =>
  ['SUPERADMIN', 'ADMIN'].includes(activeAdminTab?.name);

export const makeSelectTabOperations = createSelector(
  state => state?.auth?.userData?.user?.activeAdminTab,
  (_, tabName) => tabName,
  (activeAdminTab, tabName) => {
    if (activeAdminTab) {
      const tab = activeAdminTab.tabs.find(tab => tab.tab === tabName);
      if (isSuperRole(activeAdminTab)) {
        return FULL_ACCESS;
      }
      if (tab) {
        return operationsToBooleans(tab.operations);
      }
    }
    return null; // Return null if tab or operations are not found
  },
);

// Sub-tab keys are compared across two vocabularies that drifted apart.
//
// The backend catalogue stores slugs ('referral-channels'), and every page here
// keys its sub-tabs the same way — except Platform Settings, which uses
// SCREAMING_SNAKE ('REFERRAL_CHANNELS'). A role granted specific Platform
// Settings sub-tabs therefore matched none of them and saw an empty page, while
// a role granted the whole tab saw everything and hid the problem. Compare on a
// normalised form so either spelling resolves.
const normalizeSubTabKey = key =>
  String(key || '')
    .toLowerCase()
    .replace(/_/g, '-');

// Resolve which sub-tab keys are visible for a tab, given the full list of
// that tab's sub-tab keys (e.g. from the catalog / page config).
//   - SUPERADMIN / ADMIN            → all sub-tabs
//   - grant.allSubTabs === true     → all sub-tabs
//   - grant.subTabs non-empty       → only the granted sub-tab keys
//   - legacy grant (no sub-tab data)→ all sub-tabs (tab-level fallback)
//   - no grant for the tab          → none
export const getVisibleSubTabKeys = (
  activeAdminTab,
  tabName,
  allSubTabKeys,
) => {
  if (!activeAdminTab) return [];
  if (isSuperRole(activeAdminTab)) return allSubTabKeys;

  const tab = activeAdminTab.tabs?.find(tab => tab.tab === tabName);
  if (!tab) return [];

  if (tab.allSubTabs === true) return allSubTabKeys;

  if (tab.subTabs?.length) {
    const grantedKeys = tab.subTabs.map(sub => normalizeSubTabKey(sub.subTab));
    return allSubTabKeys.filter(key =>
      grantedKeys.includes(normalizeSubTabKey(key)),
    );
  }

  // Legacy grant with no sub-tab data → fall back to all sub-tabs.
  return allSubTabKeys;
};

// Two-level (tab + sub-tab) resolution. Mirrors makeSelectTabOperations and
// returns the same boolean shape so call sites stay symmetrical.
export const makeSelectSubTabOperations = createSelector(
  state => state?.auth?.userData?.user?.activeAdminTab,
  (_, tabName) => tabName,
  (_, __, subTabKey) => subTabKey,
  (activeAdminTab, tabName, subTabKey) => {
    if (!activeAdminTab) return null;

    if (isSuperRole(activeAdminTab)) {
      return FULL_ACCESS;
    }

    const tab = activeAdminTab.tabs.find(tab => tab.tab === tabName);
    if (!tab) return null;

    // Tab grants every sub-tab → apply tab-level operations to this sub-tab.
    if (tab.allSubTabs === true) {
      return operationsToBooleans(tab.operations);
    }

    // Explicit sub-tab grants → only the listed sub-tabs are accessible.
    if (tab.subTabs?.length) {
      const match = tab.subTabs.find(
        sub => normalizeSubTabKey(sub.subTab) === normalizeSubTabKey(subTabKey),
      );
      return match ? operationsToBooleans(match.operations) : null;
    }

    // Legacy role with no sub-tab data → fall back to tab-level operations.
    return operationsToBooleans(tab.operations);
  },
);

export default slice.reducer;
export const {
  logout,
  loginSuccess,
  handlePageError,
  setOnboardingProgress,
  handleClearPageError,
  setTempUserData,
  handleNavigateToHome,
} = slice.actions;
